import { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import app, { db } from "../firebase";

export const DEFAULT_NOTIFICATION_PREFERENCES = {
  messages: true,
  reviews: true,
  claims: true,
  verification: true,
  moderation: true,
  propertyActivity: true,
};

const NOTIFICATION_COLLECTION_LIMIT = 50;

function toSafeDocumentId(value = "") {
  return String(value || "")
    .replace(/[^a-zA-Z0-9_-]/g, "_")
    .slice(0, 180);
}

function buildNotificationId({ recipientId = "", actorId = "", type = "general", meta = {} }) {
  const target =
    meta.messageId ||
    meta.reviewId ||
    meta.reviewTargetId ||
    meta.propertyId ||
    meta.propertyTargetId ||
    meta.conversationId ||
    meta.claimId ||
    meta.verificationId ||
    Date.now();

  return toSafeDocumentId([type, recipientId, actorId, target].filter(Boolean).join("_"));
}

function normalizeActionUrl(type = "general", meta = {}) {
  if (meta.actionUrl) return String(meta.actionUrl).slice(0, 500);
  if (meta.conversationId || type === "message") return "/messages";
  if (meta.propertyId || meta.propertyTargetId || type.includes("property")) {
    return `/properties/${meta.propertyId || meta.propertyTargetId || ""}`;
  }
  if (meta.reviewId || meta.reviewTargetId || type.includes("review")) {
    return `/reviews/${meta.reviewId || meta.reviewTargetId || ""}`;
  }
  if (meta.senderUserId || meta.reviewerUserId || meta.targetUserId) {
    return `/profiles/${meta.senderUserId || meta.reviewerUserId || meta.targetUserId}`;
  }
  return "/";
}

export default function useNotifications({ user = null } = {}) {
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (!user?.uid) {
      setNotifications([]);
      return undefined;
    }

    const q = query(
      collection(db, "users", user.uid, "notifications"),
      orderBy("createdAt", "desc"),
      limit(NOTIFICATION_COLLECTION_LIMIT)
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((item) => item?.userId === user.uid || !item?.userId);
        console.log("RentDuo notifications snapshot", {
          userId: user.uid,
          total: items.length,
          unread: items.filter((item) => item?.read !== true).length,
          newestId: items[0]?.id || "",
        });
        setNotifications(items);
      },
      (error) => {
        console.error("Notifications listener error:", error);
      }
    );

    return () => unsub();
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || typeof window === "undefined") return undefined;

    let foregroundUnsubscribe = null;
    let cancelled = false;

    const setupMessaging = async () => {
      try {
        const supported = await isSupported().catch(() => false);
        if (!supported || !("Notification" in window)) return;
        if (window.Notification.permission === "denied") return;

        const permission =
          window.Notification.permission === "granted"
            ? "granted"
            : await window.Notification.requestPermission();

        if (permission !== "granted" || cancelled) return;

        const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY || "";
        if (!vapidKey) {
          console.info("Firebase Messaging skipped: REACT_APP_FIREBASE_VAPID_KEY is not configured.");
          return;
        }

        const messaging = getMessaging(app);
        let serviceWorkerRegistration = null;
        if ("serviceWorker" in navigator) {
          serviceWorkerRegistration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
        }

        const token = await getToken(messaging, {
          vapidKey,
          serviceWorkerRegistration: serviceWorkerRegistration || undefined,
        });

        if (token && !cancelled) {
          const tokenId = toSafeDocumentId(token);
          await setDoc(
            doc(db, "users", user.uid, "fcmTokens", tokenId),
            {
              token,
              userId: user.uid,
              platform: "web",
              userAgent: String(window.navigator.userAgent || "").slice(0, 240),
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }

        foregroundUnsubscribe = onMessage(messaging, (payload) => {
          console.info("RentDuo foreground push received:", payload?.notification?.title || payload?.data?.title || "Notification");
        });
      } catch (error) {
        console.error("Firebase Messaging setup failed:", error);
      }
    };

    setupMessaging();

    return () => {
      cancelled = true;
      if (foregroundUnsubscribe) foregroundUnsubscribe();
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;

    setDoc(
      doc(db, "users", user.uid, "settings", "notifications"),
      {
        notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    ).catch((error) => {
      console.error("Notification preferences bootstrap failed:", error);
    });
  }, [user?.uid]);

  const unreadNotificationCount = useMemo(
    () => notifications.filter((item) => item?.read !== true).length,
    [notifications]
  );

  const createNotification = async ({
    userId = "",
    title = "",
    body = "",
    type = "general",
    meta = {},
    emailEligible = false,
    pushEligible = true,
    inAppEligible = true,
  }) => {
    if (!userId || !title) return;
    const actorId = meta.relatedUserId || meta.senderUserId || meta.senderId || user?.uid || "";
    if (actorId && userId === actorId) {
      console.log("RentDuo notification skipped for own action", {
        userId,
        type,
      });
      return;
    }

    try {
      const notificationId = buildNotificationId({
        recipientId: userId,
        type,
        actorId,
        meta,
      });

      await setDoc(doc(db, "users", userId, "notifications", notificationId), {
        type,
        title: String(title || "").slice(0, 120),
        body: String(body || "").slice(0, 500),
        read: false,
        createdAt: serverTimestamp(),
        actionUrl: normalizeActionUrl(type, meta),
        relatedUserId: actorId,
        relatedPropertyId: meta.propertyId || meta.propertyTargetId || "",
        relatedReviewId: meta.reviewId || meta.reviewTargetId || "",
        emailEligible,
        pushEligible,
        inAppEligible,
        dedupeKey: notificationId,
        userId,
        meta,
      });
      console.log("RentDuo notification created", {
        notificationId,
        recipientId: userId,
        actorId,
        type,
      });
    } catch (error) {
      console.error("Create notification failed:", error);
    }
  };

  const markNotificationAsRead = async (notificationId = "") => {
    if (!notificationId || !user?.uid) return;
    try {
      await setDoc(
        doc(db, "users", user.uid, "notifications", notificationId),
        { read: true, readAt: serverTimestamp() },
        { merge: true }
      );
      console.log("RentDuo notification marked read", {
        userId: user.uid,
        notificationId,
      });
    } catch (error) {
      console.error("Mark notification as read failed:", error);
    }
  };

  const markAllNotificationsAsRead = async () => {
    if (!user?.uid) return;

    const unread = notifications.filter((item) => item?.read !== true);
    if (!unread.length) return;

    try {
      const batch = writeBatch(db);
      unread.forEach((item) => {
        batch.set(
          doc(db, "users", user.uid, "notifications", item.id),
          { read: true, readAt: serverTimestamp() },
          { merge: true }
        );
      });
      await batch.commit();
      console.log("RentDuo notifications marked all read", {
        userId: user.uid,
        count: unread.length,
      });
    } catch (error) {
      console.error("Mark all notifications as read failed:", error);
    }
  };

  return {
    notifications,
    unreadNotificationCount,
    createNotification,
    markNotificationAsRead,
    markAllNotificationsAsRead,
  };
}
